import { Link } from 'react-router';
import { formatDate } from '../api.ts';
import { Container } from '../components/Layout.tsx';
import { Card, KindIcon, Loading, PageTitle } from '../components/ui.tsx';
import { useApi, useTitle } from '../hooks.ts';

interface Journal {
  courseTitle: string;
  modules: {
    id: number;
    title: string;
    entries: {
      itemId: number;
      itemTitle: string;
      kind: string;
      note: string | null;
      noteUpdatedAt: string | null;
      posts: { body: string; createdAt: string; reply: boolean }[];
      submission: { text: string; submittedAt: string } | null;
    }[];
  }[];
}

function Block({ label, children }: { label: string; children: string }) {
  return (
    <div>
      <p className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</p>
      <p className="mt-1 whitespace-pre-line">{children}</p>
    </div>
  );
}

export function JournalPage() {
  useTitle('Min AI-dagbok');
  const { data, loading } = useApi<Journal>('/api/journal');
  if (loading && !data) return <Loading />;
  const empty = !data || data.modules.length === 0;
  return (
    <Container>
      <PageTitle sub="Dina anteckningar, inlägg och inlämningar samlade på ett ställe – ett eget dokument att ta med till kollegiet.">
        Min AI-dagbok
      </PageTitle>
      <div className="mb-6 flex flex-wrap gap-3">
        <a
          href="/api/journal.pdf"
          className="rounded-xl bg-night-800 px-4 py-2.5 font-semibold text-white hover:bg-night-900"
        >
          Ladda ned som PDF
        </a>
        <Link
          to="/lasning"
          className="rounded-xl bg-white px-4 py-2.5 font-semibold text-night-800 ring-1 ring-night-800/20"
        >
          Läslistan
        </Link>
      </div>
      {empty ? (
        <Card>
          <p className="text-muted">
            Dagboken är tom än så länge. Under varje del i kursen finns rutan <strong>Min dagbok</strong> där
            du kan skriva egna tankar. Dina diskussionsinlägg och inlämningar hamnar också här.
          </p>
        </Card>
      ) : (
        <div className="space-y-6">
          {data.modules.map((m) => (
            <Card key={m.id}>
              <h2 className="text-lg font-bold text-aurora-600">{m.title}</h2>
              <div className="mt-4 divide-y divide-night-100">
                {m.entries.map((e) => (
                  <article key={e.itemId} className="space-y-3 py-4 first:pt-0 last:pb-0">
                    <h3 className="flex items-center gap-2 font-bold text-night-900">
                      <KindIcon kind={e.kind} className="h-4 w-4 text-night-700" />
                      <Link to={`/kurs/del/${e.itemId}`} className="hover:underline">
                        {e.itemTitle}
                      </Link>
                    </h3>
                    {e.note && <Block label="Mina anteckningar">{e.note}</Block>}
                    {e.posts.map((p, i) => (
                      <Block
                        key={i}
                        label={`${p.reply ? 'Mitt svar' : 'Mitt inlägg'}, ${formatDate(p.createdAt)}`}
                      >
                        {p.body}
                      </Block>
                    ))}
                    {e.submission && (
                      <Block label={`Min inlämning, ${formatDate(e.submission.submittedAt)}`}>
                        {e.submission.text}
                      </Block>
                    )}
                  </article>
                ))}
              </div>
            </Card>
          ))}
        </div>
      )}
    </Container>
  );
}

interface ReadingList {
  modules: {
    id: number;
    title: string;
    locked: boolean;
    items: { id: number; title: string; estimatedMinutes: number | null; completed: boolean }[];
  }[];
}

export function ReadingListPage() {
  useTitle('Läslistan');
  const { data, loading } = useApi<ReadingList>('/api/course/reading-list');
  if (loading && !data) return <Loading />;
  const all = data?.modules.flatMap((m) => m.items) ?? [];
  const read = all.filter((i) => i.completed).length;
  return (
    <Container>
      <PageTitle sub="Frivilliga fördjupningar för dig som vill veta mer: längre texter, forskning, poddar och exempel från klassrummet.">
        Läslistan
      </PageTitle>
      {all.length === 0 ? (
        <Card>
          <p className="text-muted">Kursen har inga fördjupningar än.</p>
        </Card>
      ) : (
        <>
          <p className="mb-4 font-semibold text-night-800">
            Du har läst {read} av {all.length} fördjupningar.
          </p>
          <div className="space-y-4">
            {data!.modules.map((m) => (
              <Card key={m.id}>
                <h2 className="font-bold text-night-900">{m.title}</h2>
                <ul className="mt-3 divide-y divide-night-100">
                  {m.items.map((i) => (
                    <li key={i.id} className="flex items-center gap-3 py-2.5">
                      <span
                        className={
                          i.completed
                            ? 'flex h-6 w-6 items-center justify-center rounded-full bg-aurora-500 text-xs text-white'
                            : 'h-6 w-6 rounded-full ring-2 ring-night-800/15'
                        }
                        aria-label={i.completed ? 'Läst' : 'Inte läst'}
                      >
                        {i.completed ? '✓' : ''}
                      </span>
                      {m.locked ? (
                        <span className="flex-1 text-muted">{i.title}</span>
                      ) : (
                        <Link
                          to={`/kurs/del/${i.id}`}
                          className="flex-1 font-semibold text-night-800 hover:underline"
                        >
                          {i.title}
                        </Link>
                      )}
                      {i.estimatedMinutes ? (
                        <span className="text-sm text-muted">{i.estimatedMinutes} min</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
                {m.locked && <p className="mt-2 text-sm text-muted">Modulen öppnar senare.</p>}
              </Card>
            ))}
          </div>
        </>
      )}
    </Container>
  );
}
